"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Form from "next/form";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import {
  currentAuthenticatedUser,
  GA4DataLayer,
  GA4DataLayerFn,
  GADataLayerFn,
} from "@packages/lib/utlils/helper-function";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { deleteDocumentCookie } from "@packages/lib/utlils/cookie-utils";
import { GAConstants } from "@packages/shared-components/services/constants";
export default function PgsSearch({ pgs_search_data, gaEventName }: any) {
  const pathName = usePathname();
  const searchParam = useSearchParams();
  const [isPgsUniversityClicked, setIsPgsUniversityClicked] = useState(false);
  const [qualification, setQualification] = useState({
    qualUrl: "",
    qualCode: "",
    qualDesc: "",
  });
  const [filteredsubjectlist, setFilteredsubject] = useState([]);
  const [filteredUniversity, setFilteredUniversity] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(false);
  const [qualdropdown, setQualDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState({
    description: "",
    url: "",
    categoryCode: "",
    browseCatId: "",
    parentSubject: "",
    qualCode: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const universityClick = () => {
    setIsPgsUniversityClicked((prev) => !prev);
    setShowDropdown(true);
    setQualDropdown(true);
  };
  const [isPgsSearched, setIsPgsSearched] = useState(false);
  const router = useRouter();
  const handleKeyUp = (event: any) => {
    if (event.key === "Enter") {
      setIsPgsSearched(!isPgsSearched);
    }
  };
  function navigateTo(newUrl: string) {
    sessionStorage.removeItem("filter_param");
    deleteDocumentCookie({ name: "filter_param" });
    emitter.emit("refreshFilters");
    emitter.emit("rightMenuActionclose", "closesearch");
    window.location.href = newUrl;
  }

  function softNavigateTo(newUrl: string) {
    sessionStorage.removeItem("filter_param");
    deleteDocumentCookie({ name: "filter_param" });
    emitter.emit("refreshFilters");
    router.prefetch(newUrl);
    window.history.pushState(null, "", newUrl);
    setTimeout(() => router.push(newUrl), 0);
    setTimeout(() => emitter.emit("rightMenuActionclose", "closesearch"), 100);
  }

  useEffect(() => {
    const { description } = searchValue || {};

    // Early return if description is invalid or too short
    if (!description || description?.length < 3) {
      setFilteredsubject([]);
      setFilteredUniversity([]);
      return;
    }

    // Filter subjects first
    function filteredSubjects() {
      if (qualification.qualUrl) {
        const filteredSubjectslist = pgs_search_data?.courseDetails?.filter(
          (subjects: any) =>
            subjects?.description
              ?.toLowerCase()
              ?.includes(description?.toLowerCase()) &&
            subjects?.qual_text_key?.toLowerCase() === qualification?.qualUrl,
        );
        return filteredSubjectslist;
      } else {
        const filteredSubjectslist = pgs_search_data?.courseDetails?.filter(
          (subjects: any) =>
            subjects?.description
              ?.toLowerCase()
              ?.includes(description?.toLowerCase()) &&
            (!subjects?.qual_text_key ||
              subjects?.qual_text_key?.toLowerCase() === "null"),
        );
        return filteredSubjectslist;
      }
    }
    // Priority search function to sort filtered results based on search text position
    const prioritySearch = (list: any, searchText: any) => {
      if (!searchText) return list;

      const searchLower = searchText?.toLowerCase();

      return list
        ?.map((item: any) => ({
          ...item,
          position: item?.description?.toLowerCase()?.indexOf(searchLower),
          startsWithSearch: item?.description
            ?.toLowerCase()
            ?.startsWith(searchLower),
          exactMatch: item?.description?.toLowerCase() === searchLower,
        }))
        .filter((item: any) => item?.position !== -1) // Only include items with searchText
        .sort((a: any, b: any) => {
          if (a?.exactMatch !== b?.exactMatch) return a?.exactMatch ? -1 : 1;
          if (a?.startsWithSearch !== b?.startsWithSearch)
            return a?.startsWithSearch ? -1 : 1;
          if (a?.position !== b?.position) return a?.position - b?.position;
          return a?.description?.localeCompare(b.description);
        })
        ?.map((item: any) => ({
          description: item?.description,
          url: item?.url,
          category_code: item?.category_code,
          browse_cat_id: item?.browse_cat_id,
          parent_subject: item?.parent_subject,
          qual_Code: item?.qual_Code,
        }));
    };
    const filteredUniversity = pgs_search_data?.collegeDetails?.filter(
      (subjects: any) =>
        subjects?.collegeNameDisplay
          ?.toLowerCase()
          ?.includes(description?.toLowerCase()) ||
        subjects?.collegeNameAlias
          ?.toLowerCase()
          ?.includes(description?.toLowerCase()),
    );
    const prioritySearchcollge = (list: any, searchText: any) => {
      if (!searchText) return list;

      const searchLower = searchText?.toLowerCase();

      return list
        ?.map((item: any) => ({
          ...item,
          position: item?.collegeNameDisplay
            ?.toLowerCase()
            ?.indexOf(searchLower),
          startsWithSearch: item?.collegeNameDisplay
            ?.toLowerCase()
            ?.startsWith(searchLower),
          exactMatch: item?.collegeNameDisplay?.toLowerCase() === searchLower,
        }))

        ?.sort((a: any, b: any) => {
          if (a?.exactMatch !== b?.exactMatch) return a?.exactMatch ? -1 : 1;
          if (a?.startsWithSearch !== b?.startsWithSearch)
            return a?.startsWithSearch ? -1 : 1;
          if (a?.position !== b?.position) return a?.position - b?.position;
          return a?.collegeNameDisplay?.localeCompare(b?.collegeNameDisplay);
        })
        ?.map((item: any) => ({
          collegeId: item?.collegeId,
          collegeNameDisplay: item?.collegeNameDisplay,
          collegeNameAlias: item?.collegeNameAlias,
          collegeName: item?.collegeName,
          college_text_key: item?.college_text_key,
        }));
    };

    setFilteredUniversity(
      prioritySearchcollge(filteredUniversity, description),
    );
    setFilteredsubject(prioritySearch(filteredSubjects(), description));
  }, [searchValue, qualification]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setQualDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const keywordSearch: any = async () => {
    setIsLoading(true);
    setShowDropdown(false);
    const sanitizedDescription = searchValue?.description
      ?.trim() // Remove spaces from the front and back
      ?.replace(/[^a-zA-Z0-9\s]+/g, "-") // Replace one or more special characters with a hyphen
      ?.replace(/\s+/g, "-") // Replace spaces with hyphens
      ?.replace(/-+/g, "-") // Replace multiple consecutive hyphens with a single hyphen
      ?.replace(/^-|-$/g, "") // Remove hyphens from the start and end
      ?.toLowerCase(); // Convert the entire string to lowercase
    if (!searchValue?.description?.trim() && !qualification?.qualDesc) {
      setIsLoading(false);
      return setError(true);
    } else {
      if (qualification?.qualDesc && !searchValue?.description?.trim()) {
        GA4DataLayer({
          event: GAConstants.track_event,
          event_name: "search_event",
          study_level: qualification.qualDesc,
          subject_area: sanitizedDescription,
          search_type: "keyword",
        });
         GA4DataLayerFn({
          event: "ga_events",
          eventName: gaEventName ?? "homepage_search",
          study_level: qualification.qualDesc,
          data_label:"keyword_search",
          data_label2:sanitizedDescription
        });
        const newUrl = `${process.env.NEXT_PUBLIC_ENVIRONMENT === "prd" ? "https://www.postgraduatesearch.com" : ""}/${qualification.qualUrl}`;
        return navigateTo(newUrl);
      }
      if (searchValue?.description?.trim() && !qualification?.qualDesc) {
        GA4DataLayer({
          event: GAConstants.track_event,
          event_name: "search_event",
          study_level: qualification.qualDesc,
          subject_area: sanitizedDescription,
          search_type: "keyword",
        });
        GA4DataLayerFn({
          event: "ga_events",
          eventName: gaEventName ?? "homepage_search",
          study_level: qualification.qualDesc,
          data_label:"keyword_search",
          data_label2:sanitizedDescription
        });
        const newUrl = `${process.env.NEXT_PUBLIC_ENVIRONMENT === "prd" ? "https://www.postgraduatesearch.com" : ""}/pgs/search?keyword=${sanitizedDescription}`;
        return navigateTo(newUrl);
      }
      if (searchValue?.description?.trim() && qualification?.qualDesc) {
        GA4DataLayer({
          event: GAConstants.track_event,
          event_name: "search_event",
          study_level: qualification.qualDesc,
          subject_area: sanitizedDescription,
          search_type: "keyword",
        });
        GA4DataLayerFn({
          event: "ga_events",
          eventName: gaEventName ?? "homepage_search",
          study_level: qualification.qualDesc,
          data_label:"keyword_search",
          data_label2:sanitizedDescription
        });
        const newUrl = `${process.env.NEXT_PUBLIC_ENVIRONMENT === "prd" ? "https://www.postgraduatesearch.com" : ""}/pgs/search?keyword=${sanitizedDescription}&qualification=${qualification.qualUrl}`;
        return navigateTo(newUrl);
      }
    }
  };

  const courseLink = (e: any) => {
    const newUrl = `${process.env.NEXT_PUBLIC_ENVIRONMENT === "prd" ? "https://www.postgraduatesearch.com" : ""}${e?.url}`;
    return softNavigateTo(newUrl);
  };

  useEffect(() => {
    const bodyContainer: any = document.getElementById("bodyContainer");
    const footer: any = document.getElementById("footerComponent");
    if (!bodyContainer) {
      return;
    }

    if (
      isLoading &&
      (pathName?.includes("pgs/search")
        ? searchParam?.get("university")
        : false)
    ) {
      emitter.emit("searchpageloading", true);
      footer?.classList?.add("hidden");
      bodyContainer?.classList?.add("hidden");
    } else {
      emitter.emit("searchpageloading", false);
      footer?.classList?.remove("hidden");
      bodyContainer?.classList?.remove("hidden");
    }
    // const searchSkeletonDiv: any = document.getElementById("searchSkeleton");
    // if (!homePageContainer || !searchSkeletonDiv) {
    //   return;
    // }
    // if (isLoading) {
    //   homePageContainer.classList.add("hidden");
    //   footer.classList.add("hidden");
    //   searchSkeletonDiv.classList.remove("hidden");
    // } else {
    //   homePageContainer.classList.remove("hidden");
    //   footer.classList.remove("hidden");
    //   searchSkeletonDiv.classList.add("hidden");
    // }
  }, [isLoading]);
  return (
    <>
      <div ref={containerRef} className="flex flex-col gap-[16px]">
        <div
          className="bg-white rounded-[24px] p-[16px] border border-grey-200 hover:border-primary-500 shadow-custom-1 md:rounded-[32px] md:pl-[24px] md:p-[10px]
"
        >
          <div className="flex flex-col gap-[16px] small md:flex-row">
            <Form action={keywordSearch} className="relative grow">
              <input
                autoComplete="off"
                name="keyword"
                value={searchValue?.description}
                onChange={(e) => {
                  setSearchValue((prev: any) => ({
                    ...prev,
                    description: e.target.value
                      ?.replace(/\s{2,}/g, " ")
                      ?.trimStart(),
                    url: "",
                  }));
                  setError(false);
                  setQualDropdown(false);
                }}
                onClick={universityClick}
                onKeyUp={handleKeyUp}
                type="text"
                className="w-full focus:outline-none pt-0 pb-[16px] text-black placeholder:text-gray-500 border-b border-grey-200 md:py-[10px] md:border-none"
                aria-label="submenu"
                placeholder="Subject, qualification or university"
              />
              {isPgsUniversityClicked && showDropdown && qualdropdown && (
                <div className="flex flex-col w-[calc(100%+32px)] absolute z-[1] bg-white shadow-custom-3 rounded-[8px] left-[-16px] top-[52px] overflow-hidden md:w-[345px] custom-scrollbar-2 max-h-[250px] md:max-h-none overflow-y-auto">
                  <div className="x-small font-semibold uppercase px-[16px] py-[10px] text-neutral-700 bg-neutral-50">
                    QUALIFICATION
                  </div>
                  <ul>
                    {pgs_search_data?.studyLevelList?.map(
                      (item: any, index: any) => (
                        <li
                          onClick={() => {
                            setQualification(item);
                            universityClick();
                          }}
                          key={index}
                          className="px-[16px] py-[10px] block hover:bg-blue-50 hover:underline cursor-pointer"
                        >
                          {item?.qualDesc}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
              {showDropdown && searchValue?.description?.length > 2 && (
                <div
                  //   ref={containerRef}
                  className="flex flex-col w-[calc(100%+32px)] absolute z-[1] bg-white shadow-custom-3 rounded-[8px] left-[-16px] top-[52px] overflow-hidden custom-scrollbar-2 max-h-[250px] overflow-y-auto"
                >
                  {searchValue?.description?.length > 2 && (
                    <div
                      onClick={() => {
                        setIsLoading(true);
                        keywordSearch();
                      }}
                    >
                      <div className="px-[16px] py-[12px]">
                        <p className="x-small font-semibold text-black tracking-[1px] leading-[18px]">
                          KEYWORD SEARCH FOR
                        </p>
                        <p className="small text-primary-400">
                          {` '${searchValue?.description}'`}
                        </p>
                      </div>
                    </div>
                  )}
                  {filteredsubjectlist?.length > 0 && (
                    <>
                      <div className="x-small font-semibold uppercase px-[16px] py-[10px] text-neutral-700 bg-neutral-50">
                        COURSES
                      </div>
                      <ul>
                        {filteredsubjectlist?.map((item: any, index) => (
                          <div
                            key={index}
                            onClick={async () => {
                              setIsLoading(true);
                              setSearchValue(item);
                              setShowDropdown(false);
                              GA4DataLayer({
                                event: GAConstants.track_event,
                                event_name: "search_event",
                                study_level: qualification?.qualDesc,
                                subject_area: item?.parentSubject
                                  ? item?.parentSubject
                                  : item?.description,
                                subject_discipline: item?.parentSubject
                                  ? item?.description
                                  : "NA",
                                search_type: "drop_down",
                              });
                              GA4DataLayerFn({
          event: "ga_events",
          eventName: gaEventName ?? "homepage_search",
          study_level: qualification.qualDesc,
          data_label:"subject_search",
          cpe_parent_subject: item?.parentSubject
                                  ? item?.parentSubject
                                  : item?.description,
                                cpe_child_subject: item?.parentSubject
                                  ? item?.description
                                  : "NA",
        });
                              courseLink(item);
                            }}
                            className="px-[16px] py-[10px] block hover:bg-blue-50  hover:underline cursor-pointer"
                          >
                            <span className="text-grey900 ">
                              {item?.description}
                            </span>{" "}
                            {/* <span className="text-grey-700">{item.course}</span> */}
                          </div>
                        ))}
                      </ul>
                    </>
                  )}
                  {filteredUniversity?.length > 0 && (
                    <>
                      <div className="x-small font-semibold uppercase px-[16px] py-[10px] text-neutral-700 bg-neutral-50">
                        INSTITUTIONS
                      </div>
                      <ul>
                        {filteredUniversity?.map((item: any, index) => (
                          <a
                            href={`/universities/${item?.college_text_key}`}
                            key={index}
                            onClick={async () => {
                              setShowDropdown(false);
                              GA4DataLayer({
                                event: GAConstants.track_event,
                                event_name: "search_event",
                                search_type: "drop_down",
                                institution_name: item?.collegeNameDisplay,
                                institution_id: item?.collegeId,
                              });
                              GA4DataLayerFn({
                                  event: "ga_events",
                                  eventName: gaEventName ?? "homepage_search",
                                  study_level: qualification.qualDesc,
                                  data_label:"university_search",
                                  college_id: item?.collegeId,
                                  college_name:item?.collegeNameDisplay                     
                            });
                            }}
                            className="px-[16px] py-[10px] block hover:bg-blue-50  hover:underline cursor-pointer"
                          >
                            <span className="text-grey900">
                              {item?.collegeNameDisplay}
                            </span>{" "}
                            {/* <span className="text-grey-700">{item.course}</span> */}
                          </a>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </Form>
            {qualification?.qualDesc && (
              <div className="flex items-center justify-between gap-[4px] bg-gray-100 text-grey-500 font-semibold px-[12px] py-[8px] rounded-[4px]">
                <span>{qualification?.qualDesc}</span>

                <svg
                  className="cursor-pointer"
                  onClick={() =>
                    setQualification({
                      qualUrl: "",
                      qualCode: "",
                      qualDesc: "",
                    })
                  }
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18"
                    stroke="#1f2937"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 6L18 18"
                    stroke="#1f2937"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}

            <button
              type="submit"
              onClick={() => keywordSearch()}
              className="btn btn-primary w-full flex items-center justify-center gap-[6px] px-[24px] py-[10px] md:w-[114px]"
            >
              {!isLoading && (
                <Image
                  src="/static/assets/icons/search_icon.svg"
                  width="18"
                  height="18"
                  alt="Search icon"
                />
              )}
              Search
              {isLoading && (
                <svg
                  className="animate-spin"
                  width="17"
                  height="16"
                  viewBox="0 0 65 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M64.5 32C64.5 49.6731 50.1731 64 32.5 64C14.8269 64 0.5 49.6731 0.5 32C0.5 14.3269 14.8269 0 32.5 0C50.1731 0 64.5 14.3269 64.5 32ZM8.5 32C8.5 45.2548 19.2452 56 32.5 56C45.7548 56 56.5 45.2548 56.5 32C56.5 18.7452 45.7548 8 32.5 8C19.2452 8 8.5 18.7452 8.5 32Z"
                    fill="#C8F0F5"
                  />
                  <path
                    d="M14.1456 5.78713C18.2822 2.89061 23.0457 1.01424 28.0465 0.311421C33.0473 -0.391393 38.1434 0.0993091 42.9182 1.74341C47.693 3.38751 52.0109 6.13832 55.5189 9.77094C59.0269 13.4036 61.6253 17.8148 63.1018 22.6441C64.5782 27.4734 64.8908 32.5835 64.0138 37.5567C63.1369 42.53 61.0955 47.225 58.0563 51.2581C55.0172 55.2911 51.0667 58.5477 46.5279 60.7614C41.989 62.9752 36.9907 64.0833 31.9415 63.9951L32.0811 55.9963C35.868 56.0624 39.6168 55.2314 43.0209 53.5711C46.425 51.9108 49.3879 49.4684 51.6672 46.4436C53.9466 43.4188 55.4777 39.8975 56.1354 36.1676C56.7931 32.4377 56.5587 28.605 55.4513 24.9831C54.344 21.3611 52.3951 18.0527 49.7642 15.3282C47.1332 12.6037 43.8947 10.5406 40.3136 9.30756C36.7325 8.07448 32.9104 7.70646 29.1598 8.23357C25.4093 8.76068 21.8367 10.168 18.7342 12.3403L14.1456 5.78713Z"
                    fill="#0086F8"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
        {error && (
          <p className="small text-negative-default">
            Enter subject, qualification or university
          </p>
        )}
      </div>
    </>
  );
}
